import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  linearProgressClasses,
  styled,
  useTheme,
} from '@mui/material';
import { StyledPaperItem } from '../common/mui-components';
import { fetchGet, fetchPost } from '../api/common';
import { Maintenance } from '../components/room/types';
import { DemonButton } from '../common/components/demon-button/DemonButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Countdown, { zeroPad } from 'react-countdown';

const CATEGORY_ABBR = [
  { display: 'A&A', full: 'Art & Architecture' },
  { display: 'Biz', full: 'Business' },
  { display: 'ClsM', full: 'Classical Music' },
  { display: 'Cult', full: 'Culture' },
  { display: 'F&D', full: 'Food & Drink' },
  { display: 'Film', full: 'Film' },
  { display: 'Geo', full: 'Geography' },
  { display: 'His', full: 'History' },
  { display: 'Lit', full: 'Literature' },
  { display: 'Math', full: 'Math' },
  { display: 'Nich', full: 'Niche' },
  { display: 'P&C', full: 'Politics & Civics' },
  { display: 'P&R', full: 'Philosophy & Religion' },
  { display: 'PopM', full: 'Pop Music' },
  { display: 'Sci', full: 'Science' },
  { display: 'Sprt', full: 'Sports' },
  { display: 'Thtr', full: 'Theater' },
  { display: 'TV', full: 'TV' },
  { display: 'V&L', full: 'Vocab & Language' },
  { display: 'VG', full: 'Video Games' },
];

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    marginLeft: '20px',
    marginRight: '20px',
    '&.Mui-disabled': {
      border: 0,
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.shape.borderRadius,
      border: '1px solid',
      borderColor: theme.palette.demon.contrastText,
    },
    '&:first-of-type': {
      borderRadius: theme.shape.borderRadius,
    },
    '&.Mui-selected': {
      border: '1px solid',
      borderColor: theme.palette.demon.main,
    },
  },
}));

type Demon = {
  answeredAt: number; // the second they answered
  category: string;
  questionId: string;
  seenAt: number;
};

export type DemonsData = {
  id: string;
  profileId: string;
  lastDemonIndex: number;
  demons: Demon[];
  vanquished: string[];
  demonsOnCooldown: Demon[];
  demonsOffCooldownBy: number;
};

export const vanquishProgress = (demonsData: DemonsData) => {
  if (!demonsData || !demonsData.vanquished) return 0;
  return Math.round(
    (demonsData.vanquished.length /
      (demonsData.vanquished.length + demonsData.demons.length)) *
      100
  );
};

type PropTypes = {
  allowSpectators: boolean;
  bannedDemon: string;
  close: () => void;
  demonGamePlayedToday: boolean;
  demonsData: DemonsData;
  fetchDemonsData: () => void;
  handlePlayDemonsClick: () => void;
  maintenance: Maintenance;
  onUpdateProfile: () => void;
  open: boolean;
  profileId: string;
  totalDemonGamesPlayed: number;
};

export const FaceYourDemonsDialog = ({
  allowSpectators,
  bannedDemon,
  close,
  demonGamePlayedToday,
  demonsData,
  fetchDemonsData,
  handlePlayDemonsClick,
  maintenance,
  onUpdateProfile,
  open,
  profileId,
  totalDemonGamesPlayed,
}: PropTypes) => {
  const theme = useTheme();
  const [category, setCategory] = useState<string>();

  useEffect(() => {
    if (!open) return;
    fetchDemonsData();
  }, [open]);

  useEffect(() => {
    if (!bannedDemon) return;
    const category = CATEGORY_ABBR.find((c) => c.full === bannedDemon);
    setCategory(category.display);
  }, [bannedDemon]);

  const handleBanCategory = async (
    event: React.MouseEvent<HTMLElement>,
    newCategory: string
  ) => {
    console.log('newCategory', newCategory);
    setCategory(newCategory);
    if (newCategory) {
      const category = CATEGORY_ABBR.find((c) => c.display === newCategory);
      await updateBannedDemon(category.full);
    } else {
      await updateBannedDemon(null);
    }
  };

  const updateBannedDemon = async (category: string | null) => {
    const res = await fetchPost('/demons/update-banned-demon', {
      profileId: profileId,
      bannedDemon: category,
    });
    fetchDemonsData();
  };

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 20,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: 'black',
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      // backgroundColor: '#6d140e',
      background:
        'linear-gradient(106deg, rgba(255,255,255,1) 0%, rgba(109,20,14,1) 33%, rgba(109,20,14,1) 100%)',
    },
  }));

  const countdownRenderer = ({ days, hours, minutes, seconds }: any) => {
    return (
      <span>
        {days}:{zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const handleAllowSpectators = async (check: boolean) => {
    const updatedProfile = {
      profileId: profileId,
      allowSpectators: check,
    };
    await fetchPost('/demons/update-allow-spectators', updatedProfile);
    onUpdateProfile();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md">
      <DialogTitle>
        <Typography
          variant="h5"
          textAlign={'center'}
          fontFamily={'requiem'}
          letterSpacing={5}
          sx={{
            textShadow: `0 0 4px ${theme.palette.demon.main},
0 0 4px ${theme.palette.demon.main},
0px 0px 16px ${theme.palette.demon.main}`,
          }}
        >
          FACE YOUR DEMONS
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Grid container spacing={2} className="CONTAINER-MAIN">
          <Grid item xs={12} sm={12} className="MID">
            <StyledPaperItem>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography
                    width={'100%'}
                    textAlign={'center'}
                    fontWeight={600}
                    sx={{
                      color: theme.palette.demon.contrastText,
                    }}
                  >
                    Why do I have demons?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack
                    spacing={2}
                    mb={2}
                    sx={{
                      color: theme.palette.demon.contrastText,
                    }}
                  >
                    <Typography fontWeight={600}>
                      Well, don't we all? But in PAR, demons are questions you
                      didn't fare so well on whereas others did.
                    </Typography>
                    <Typography fontWeight={600}>
                      We analyzed your previously played questions where:
                    </Typography>
                    <Stack>
                      <Typography fontWeight={600}>
                        - at least 10 players have seen the question
                      </Typography>
                      <Typography fontWeight={600}>
                        - at least 3 players scored under PAR
                      </Typography>
                      <Typography fontWeight={600}>
                        - you performed in the bottom 20%
                      </Typography>
                    </Stack>
                    <Typography fontWeight={600}>
                      This is a good opportunity to fill some knowledge gaps.
                      Face as many demons as you can in this Daily Survival
                      Endurance mode. You get 3 lives (time-outs) then game
                      over.
                    </Typography>
                    <Typography fontWeight={600}>
                      Vanquish a demon by scoring in the top 50%.
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </StyledPaperItem>
          </Grid>
          <Grid item xs={12} sm={12} className="CONTAINER-TOP">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} className="DESCRIPTION">
                <StyledPaperItem>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        border: '3px solid',
                        borderColor: 'darkgrey',
                        borderRadius: '5px',
                      }}
                    >
                      <BorderLinearProgress
                        variant="determinate"
                        value={vanquishProgress(demonsData)}
                      />
                    </Box>
                    <Typography
                      sx={{
                        color: theme.palette.demon.light,
                        fontFamily: 'requiem',
                        fontWeight: 600,
                        letterSpacing: 2,
                      }}
                    >
                      Vanquished{' '}
                      <Typography
                        variant="h6"
                        sx={{
                          display: 'inline',
                          color: theme.palette.demon.contrastText,
                          fontFamily: 'requiem',
                        }}
                      >
                        {demonsData?.vanquished?.length || 0}
                      </Typography>{' '}
                      out of{' '}
                      <Typography
                        variant="h6"
                        sx={{
                          display: 'inline',
                          color: theme.palette.demon.contrastText,
                          fontFamily: 'requiem',
                        }}
                      >
                        {(demonsData?.demons?.length || 0) +
                          (demonsData?.vanquished?.length || 0) +
                          (demonsData?.demonsOnCooldown?.length || 0)}
                      </Typography>{' '}
                      demons
                    </Typography>
                  </Stack>
                </StyledPaperItem>
              </Grid>
            </Grid>
          </Grid>
          {demonsData?.demonsOnCooldown?.length > 0 ? (
            <Grid item xs={12} sm={12} className="CONTAINER-TOP">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} className="DESCRIPTION">
                  <StyledPaperItem>
                    <Stack
                      direction={'row'}
                      spacing={2}
                      sx={{ display: 'flex', justifyContent: 'center' }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.demon.main,
                          fontFamily: 'requiem',
                          fontWeight: 600,
                          letterSpacing: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            display: 'inline',
                            color: theme.palette.demon.contrastText,
                            fontFamily: 'requiem',
                          }}
                        >
                          {demonsData?.demonsOnCooldown.length || 0}
                        </Typography>{' '}
                        demons have escaped and will return in
                      </Typography>
                      {demonsData ? (
                        <Typography
                          variant="h6"
                          sx={{
                            display: 'inline',
                            color: theme.palette.demon.contrastText,
                            fontWeight: 600,
                          }}
                        >
                          <Countdown
                            date={demonsData.demonsOffCooldownBy}
                            renderer={countdownRenderer}
                          />
                        </Typography>
                      ) : null}
                    </Stack>
                  </StyledPaperItem>
                </Grid>
              </Grid>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={12} className="CONTAINER-TOP">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} className="DESCRIPTION">
                <StyledPaperItem>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography
                        width={'100%'}
                        textAlign={'center'}
                        fontWeight={600}
                        sx={{
                          color: theme.palette.demon.contrastText,
                        }}
                      >
                        Demon Immunity
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Typography
                          variant="caption"
                          width={'100%'}
                          textAlign={'center'}
                          sx={{
                            color: theme.palette.primary.main,
                          }}
                        >
                          Ban a category to ignore those demons
                        </Typography>
                        <StyledToggleButtonGroup
                          size="small"
                          value={category}
                          exclusive
                          onChange={handleBanCategory}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {CATEGORY_ABBR.slice(0, 10).map((c) => (
                            <ToggleButton
                              sx={{
                                border: '1px solid',
                                borderColor: theme.palette.demon.contrastText,
                                width: 50,
                              }}
                              key={c.display}
                              value={c.display}
                            >
                              <Tooltip title={c.full} placement="top">
                                <Box>{c.display}</Box>
                              </Tooltip>
                            </ToggleButton>
                          ))}
                        </StyledToggleButtonGroup>
                        <StyledToggleButtonGroup
                          size="small"
                          value={category}
                          exclusive
                          onChange={handleBanCategory}
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {CATEGORY_ABBR.slice(10, 20).map((c) => (
                            <ToggleButton
                              sx={{
                                border: '1px solid',
                                borderColor: theme.palette.demon.contrastText,
                                width: 50,
                              }}
                              key={c.display}
                              value={c.display}
                            >
                              <Tooltip title={c.full} placement="top">
                                <Box>{c.display}</Box>
                              </Tooltip>
                            </ToggleButton>
                          ))}
                        </StyledToggleButtonGroup>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </StyledPaperItem>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} className="CONTAINER-TOP">
            <Stack
              direction={'row'}
              spacing={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowSpectators}
                    onChange={(e) => handleAllowSpectators(e.target.checked)}
                  />
                }
                label="Allow spectators"
                labelPlacement="start"
              />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" width={'100%'}>
          <Box width={'50%'} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="text" onClick={close} fullWidth>
              Cancel
            </Button>
          </Box>
          <Box width={'50%'}>
            {maintenance.scheduled ? (
              <DemonButton
                disabled
                handleDemonButtonClick={handlePlayDemonsClick}
                hideFire
                label={`PAR is down`}
              />
            ) : !demonsData?.demons?.length &&
              !demonsData?.vanquished?.length ? (
              <DemonButton
                disabled
                handleDemonButtonClick={handlePlayDemonsClick}
                hideFire
                label={`No demons, yet`}
              />
            ) : demonGamePlayedToday ? (
              <DemonButton
                disabled
                handleDemonButtonClick={handlePlayDemonsClick}
                hideFire
                label={`demons await tomorrow`}
              />
            ) : vanquishProgress(demonsData) === 100 ? (
              <DemonButton
                disabled
                handleDemonButtonClick={handlePlayDemonsClick}
                hideFire
                label={`You've prevailed, for now`}
              />
            ) : (
              <DemonButton
                handleDemonButtonClick={handlePlayDemonsClick}
                label={'Face Your Demons'}
              />
            )}{' '}
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
